import React, { useMemo, useState } from 'react';
import {
  deleteUploadedLeden,
  downloadLedenTemplate,
  parseLedenExcel,
  uploadLedenForConviventie
} from '../services/ledenUploadService';
import { trackLedenDownloaded, trackLedenUploaded, trackLedenUploadFailed } from '../services/analytics';
import { APP_ERROR_CONTEXTS, describeAppError, formatAppErrorAlert, reportAppError } from '../services/appErrors';
import './LedenUploadPanel.css';

/**
 * Builds the download file name for a ledenlijst download (Feature 36):
 * `ledenlijst-<downloadName>-<yyyy-mm-dd>.xlsx`, slugged so it is safe on every
 * OS. `downloadName` is the full "Bisdom - Parochie - Gemeenschap" definition of
 * the target (see `formatGemeenschapFullLabel`); the date is built from local
 * parts (no UTC off-by-one).
 */
const buildLedenFilename = (downloadName) => {
  const slug = String(downloadName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const today = new Date();
  const stamp = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0')
  ].join('-');

  return `ledenlijst-${slug || 'gemeenschap'}-${stamp}.xlsx`;
};

const formatUploadedAt = (value) => {
  if (!value) return '-';

  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('nl-NL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const LedenUploadPanel = ({
  conviventieId,
  targetId,
  targetType,
  targetLabel,
  downloadName,
  leden = [],
  uploadStatus = 'pending',
  uploadedAt,
  uploadedBy,
  expiresAt,
  onUploaded,
  onDeleted
}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState({ leden: [], errors: [], warnings: [] });
  const [loading, setLoading] = useState(false);

  const hasBlockingErrors = preview.errors.length > 0;
  // Feature 36: a target that already has leden downloads them pre-filled.
  const hasExistingLeden = Array.isArray(leden) && leden.length > 0;

  const statusLabel = useMemo(() => {
    if (uploadStatus === 'confirmed') return 'Bevestigd';
    if (uploadStatus === 'uploaded') return 'Ledenlijst geupload';
    return 'Wacht op ledenlijst';
  }, [uploadStatus]);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);

    if (!selectedFile) {
      setPreview({ leden: [], errors: [], warnings: [] });
      return;
    }

    setLoading(true);
    try {
      const parsed = await parseLedenExcel(selectedFile);
      setPreview(parsed);
    } catch (error) {
      console.error('Error parsing leden upload:', error);
      trackLedenUploadFailed({ error_count: 1 });
      setPreview({
        leden: [],
        errors: [{ row: null, field: 'bestand', message: 'Fout bij het lezen van het bestand' }],
        warnings: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || preview.leden.length === 0 || hasBlockingErrors) return;

    if (uploadStatus === 'confirmed') {
      const accepted = window.confirm(
        'Er is al een bevestiging voor deze lijst. Opnieuw uploaden wist de bevestiging (inclusief aankomst- en vertrekdatums). Doorgaan?'
      );
      if (!accepted) return;
    }

    setLoading(true);
    try {
      await uploadLedenForConviventie(
        conviventieId,
        targetId,
        targetType,
        preview.leden,
        { expiresAt }
      );
      trackLedenUploaded({
        row_count: preview.leden.length,
        target: targetType === 'guestGroup' ? 'gastengroep' : 'gemeenschap'
      });

      setFile(null);
      setPreview({ leden: [], errors: [], warnings: [] });
      await onUploaded?.();
    } catch (error) {
      console.error('Error uploading ledenlijst:', error);
      trackLedenUploadFailed({ error_count: preview.errors.length || 1 });
      const info = describeAppError(error, APP_ERROR_CONTEXTS.LEDEN_UPLOAD);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const accepted = window.confirm('Weet u zeker dat u de geuploade ledenlijst wilt verwijderen?');
    if (!accepted) return;

    setLoading(true);
    try {
      await deleteUploadedLeden(conviventieId, targetId, targetType);
      setFile(null);
      setPreview({ leden: [], errors: [], warnings: [] });
      await onDeleted?.();
    } catch (error) {
      console.error('Error deleting uploaded ledenlijst:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.LEDEN_DELETE);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    } finally {
      setLoading(false);
    }
  };

  // Feature 36: download the template for this target — pre-filled with the leden
  // currently shown when a ledenlijst already exists, blank otherwise.
  const handleDownloadTemplate = async () => {
    const target = targetType === 'guestGroup' ? 'gastengroep' : 'gemeenschap';

    setLoading(true);
    try {
      if (hasExistingLeden) {
        await downloadLedenTemplate(buildLedenFilename(downloadName || targetLabel), leden);
      } else {
        await downloadLedenTemplate();
      }

      trackLedenDownloaded({
        row_count: hasExistingLeden ? leden.length : 0,
        target
      });
    } catch (error) {
      console.error('Error downloading leden template:', error);
      const info = describeAppError(error, APP_ERROR_CONTEXTS.LEDEN_DOWNLOAD);
      reportAppError(info, { error });
      alert(formatAppErrorAlert(info));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="upload-inline-panel upload-panel-embedded"
      aria-label="Leden upload panel"
    >
      <div className="upload-inline-header">
        <div>
          <h3>Ledenlijst - {targetLabel}</h3>
          <p>Status: {statusLabel}</p>
          {uploadedAt && (
            <p>Geupload op: {formatUploadedAt(uploadedAt)} door {uploadedBy || '-'}</p>
          )}
        </div>
      </div>

      <div className="upload-inline-actions-row">
        <button
          type="button"
          className="button button-secondary"
          onClick={handleDownloadTemplate}
          disabled={loading}
          title={
            hasExistingLeden
              ? 'Download de huidige ledenlijst als Excel-bestand'
              : 'Download een leeg Excel-template'
          }
        >
          {hasExistingLeden ? 'Download ledenlijst' : 'Download template'}
        </button>

        <label className="upload-file-label">
          Kies bestand
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={loading}
          />
        </label>

        <button
          type="button"
          className="button button-primary"
          disabled={loading || !file || preview.leden.length === 0 || hasBlockingErrors}
          onClick={handleUpload}
        >
          Upload ledenlijst
        </button>

        {uploadStatus !== 'pending' && (
          <button
            type="button"
            className="button button-danger"
            disabled={loading}
            onClick={handleDelete}
          >
            Verwijder upload
          </button>
        )}
      </div>

      {file && (
        <div className="upload-preview-box">
          <p>Bestand: {file.name}</p>
          <p>Rijen gelezen: {preview.leden.length}</p>
          {preview.errors.length > 0 && (
            <ul className="upload-errors">
              {preview.errors.map((error, index) => (
                <li key={`${error.field}-${index}`}>
                  Rij {error.row || '-'} - {error.field}: {error.message}
                </li>
              ))}
            </ul>
          )}
          {preview.warnings.length > 0 && (
            <ul className="upload-warnings">
              {preview.warnings.map((warning, index) => (
                <li key={`${warning.field}-${index}`}>
                  Rij {warning.row || '-'} - {warning.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};

export default LedenUploadPanel;